import contentData from './content_data.json'
import { questionById } from './questions'
import type { Answers, ProposalType } from './types'

function textOf(questionId: string, value: string): string {
  const q = questionById(questionId)
  return q.options?.find((o) => o.value === value)?.text ?? value
}

function textsOf(questionId: string, values: string[]): string[] {
  return values.map((v) => textOf(questionId, v))
}

const Q6_SUBJECT: Record<string, string> = {
  q6_full_agency: '代理店に全面的に任せる形',
  q6_agency_led: '代理店主導で社内は報告を受ける形',
  q6_shared: '代理店と社内で分担する形',
  q6_inhouse: '社内だけ',
  q6_notyet: '',
}

// §8 ②「いま起きていること」の生成テンプレート
export function buildSummary(a: Answers): string {
  if (a.Q1 === 'q1_unsure') {
    const weak = textsOf('Q5', a.Q5).filter((t) => t !== '全体的に自信がない')
    if (weak.length > 0) {
      return `${weak.slice(0, 2).join('、')}に、特に不安を感じている状態です。`
    }
    return 'まだ課題が整理できておらず、何から着手すべきか判断できていない状態です。'
  }

  const measures = a.Q4.filter((v) => v !== 'q4_none')
  const symptoms = a.Q2.slice(0, 2)
  const subject = Q6_SUBJECT[a.Q6] ?? ''

  if (measures.length > 0 && symptoms.length > 0 && subject) {
    const measureText = textsOf('Q4', measures).join('と')
    return `${measureText}を${subject}で進めているものの、${symptoms.join('、')}という状態です。`
  }

  if (symptoms.length > 0) {
    return `${symptoms.join('、')}という状態です。`
  }

  return `${textOf('Q1', a.Q1)}という状態です。`
}

const BOTTLENECK_TEXT: Record<ProposalType, string> = {
  A: '配信・設定の基礎は既にできています。ボトルネックは、成果差がいちばん大きい領域が、いちばん手つかずのまま残っていることです。得意な部分をどれだけ伸ばしても、苦手な領域が成果の天井を作ります。',
  B: 'ボトルネックは、施策の知識より先に「何を目標に置くか」が決まっていないことです。個別の手法から入ると、施策は増えても成果の判断ができません。戦略とKPIの設計を先に済ませることで、その後の学習効率が大きく変わります。',
  C: 'ボトルネックは、知識の不足ではなく「自社のアカウントで判断した経験」が無いことです。判断基準は、自社の商材・季節性・競合状況の中でしか育ちません。',
  D: 'ボトルネックは、スキルの不足ではなく「判断基準が組織で共有されていない」ことです。全員に同じ研修を受けさせても差は埋まりません。役割ごとに必要な深さが違うためです。',
}

const M1_SUFFIX =
  'なお、アカウントの中身が見えない状態では研修の効果も限定的になります。受講前に管理権限を自社で取得しておくことを推奨します。'

export function buildBottleneck(type: ProposalType, hasM1: boolean): string {
  return hasM1 ? `${BOTTLENECK_TEXT[type]}\n\n${M1_SUFFIX}` : BOTTLENECK_TEXT[type]
}

// 以下のブロック文言は content_data.json を正本とする（Ruby側のレポートページと共有）。
export const M2_BLOCK = contentData.blocks.m2
export const M3_BLOCK = contentData.blocks.m3
export const M5_BLOCK = contentData.blocks.m5
export const RELIEF_ITEMS = contentData.blocks.reliefItems
export const CTA_SUBTEXT = contentData.blocks.ctaSubtext
export const M6_LINE = contentData.blocks.m6Line
